import {useCallback} from 'react';
import {
    EventAction,
    EventCategory,
    EventCurrencyCodes,
    EventLabel,
    EventNames,
    OWOX_PAGES,
} from 'modules/google-analytics/constants';
import {useGtagEventLocation} from '../helpers/useGtagEventLocation';
import {IPremiumServiceBanner} from 'modules/home/models/PremiumService';
import {ICatalogBanner} from 'modules/products/types';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';
import {IUseSliderBannerViewTrackingParams} from 'hooks/analytics/gtag/sliders/useSliderBannerViewTracking';

interface ICommonBanner extends ICatalogBanner {
    id: string;
    name?: string;
    url?: string;
    hint?: {text: string; bgColor: string};
    linkTarget?: string;
    images?: {
        desktop: string;
        tablet: string;
        mobile: string;
    };
}

interface ICommonBannerWithPosition extends ICommonBanner {
    position: number;
}

type TBannerClickType = 'common' | 'premium';

interface IUseBannerClickGtagParams {
    type: TBannerClickType;
    banner: Partial<ICommonBannerWithPosition | IPremiumServiceBanner>;
    eventLocation?: keyof typeof OWOX_PAGES;
    creative?: IUseSliderBannerViewTrackingParams<any>['creative'];
    enabled?: boolean;
}

export const useBannerClickGtag = () => {
    const {eventLocation: defaultLocation} = useGtagEventLocation();

    return useCallback(
        // eslint-disable-next-line complexity
        ({enabled = true, ...params}: IUseBannerClickGtagParams) => {
            if (!enabled) {
                return;
            }

            const {creative, eventLocation = defaultLocation} = params;

            if ('common' === params.type) {
                const banner = params.banner as ICommonBannerWithPosition;
                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventCategory: EventCategory.Interactions,
                    eventAction: EventAction.Click,
                    eventLabel: EventLabel.Banner,
                    eventContent: banner.sanitizedTitle ?? banner.name ?? undefined,
                    eventPosition: banner.position || 1,
                    eventLocation,
                    ecommerce: {
                        currencyCode: EventCurrencyCodes.RUB,
                        promoClick: {
                            promotions: [
                                {
                                    id: banner.id,
                                    name: banner.sanitizedTitle ?? banner.name ?? undefined,
                                    creative,
                                    position: banner.position || 1,
                                },
                            ],
                        },
                    },
                });
            }
            if ('premium' === params.type) {
                const banner = params.banner as IPremiumServiceBanner;
                sendGtagEvent({
                    event: EventNames.OWOX,
                    eventCategory: EventCategory.Interactions,
                    eventAction: EventAction.Click,
                    eventLabel: EventLabel.PriveCard,
                    eventValue: banner.button.link,
                    eventContent: banner.title,
                    eventContext: 'null',
                    eventLocation: eventLocation || OWOX_PAGES.MAIN,
                });
            }
        },
        [defaultLocation],
    );
};
